import React, { useState } from 'react';
import { formatPlaceName } from '@/lib/utils';
import { Button } from '@/components/shared/button';
import { useSaveBenchmarkComparison } from '@/hooks/useSaveBenchmarkComparison';
import { Place } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { TabId, TAB_IDS } from '../types/data';
import { JurisdictionSnapshotSection } from './JurisdictionSnapshotSection';
import { ShareOptionsPopup } from './ShareOptionsPopup';
import { useShareUrl } from '@/hooks/useShareUrl';
import { Tabs, TabsList, TabsTrigger } from '@/components/shared/tabs';
import { copyToClipboardAndToast } from '@/utils/shareUtils';

interface TabsSectionProps {
  selectedPlace: Place | null;
  peerPlaces: Place[];
  metricsData?: Record<string, unknown>;
  activeTab: TabId;
  onTabChange: (tabId: TabId) => void;
}

export const TabsSection: React.FC<TabsSectionProps> = ({ 
  selectedPlace, 
  peerPlaces, 
  metricsData,
  activeTab,
  onTabChange
}) => {
  const { toast } = useToast();
  const formattedPlaceName = selectedPlace ? formatPlaceName(selectedPlace.name) : '{Jurisdiction}';

  // Share link derived from current URL
  const shareLink = useShareUrl();

  const { handleSave, isSaving } = useSaveBenchmarkComparison({
    selectedPlace: selectedPlace!,
    peerPlaces,
    activeTab,
    metricsData,
  });

  const onSaveClick = async () => {
    if (!selectedPlace) {
      toast({
        title: "Cannot save comparison",
        description: "Please select a jurisdiction first",
        variant: "destructive",
      });
      return;
    }

    try {
      await handleSave();
      toast({
        title: "Success",
        description: "Your comparison has been saved",
      });
    } catch (error) {
      console.error('Error saving comparison:', error);
      toast({
        title: "Error",
        description: "Failed to save comparison. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleShareClick = async () => {
    await copyToClipboardAndToast(shareLink, 'Benchmarking Results');
  };

  const tabs = [
    { 
      id: TAB_IDS.PEER_COMPARISON, 
      label: 'Peer Comparison', 
      icon: 'groups',
      active: activeTab === TAB_IDS.PEER_COMPARISON
    },
    { 
      id: TAB_IDS.JURISDICTION_SNAPSHOT, 
      label: `${formattedPlaceName} Snapshot`, 
      icon: 'camera',
      active: activeTab === TAB_IDS.JURISDICTION_SNAPSHOT
    },
    { 
      id: TAB_IDS.JURISDICTION_OVERTIME, 
      label: `${formattedPlaceName} Over Time`, 
      icon: 'history',
      active: activeTab === TAB_IDS.JURISDICTION_OVERTIME
    },
  ];

  return (
    <>
      <section className="bg-white w-full py-12 sm:py-16 md:py-20">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
          <div className="w-full">
            <Tabs
              value={activeTab}
              onValueChange={(val) => onTabChange(val as TabId)}
            >
              <TabsList
                className="bg-neutral-100 flex w-full items-center gap-1 text-lg sm:text-xl md:text-2xl text-[#212121] font-medium leading-[1.3] justify-center flex-col lg:flex-row rounded-[20px] p-1 font-geist h-auto"
                aria-label="Benchmark view selection"
              >
                {tabs.map((tab, index) => (
                  <React.Fragment key={tab.id}>
                    <TabsTrigger
                      value={tab.id}
                      className="w-full lg:flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 lg:px-10 py-4 sm:py-5 md:py-6 rounded-[20px] transition-colors text-sm sm:text-base md:text-lg lg:text-xl border lg:border-0 data-[state=inactive]:border-[#CA0079] data-[state=active]:border-transparent data-[state=active]:text-white data-[state=active]:bg-[#CA0079] hover:bg-gray-200"
                    >
                      <span className="material-symbols-outlined flex items-center justify-center text-xl sm:text-2xl flex-shrink-0" aria-hidden="true">
                        {tab.icon}
                      </span>
                      <span className="truncate flex items-center">{tab.label}</span>
                    </TabsTrigger>
                    {index < tabs.length - 1 && (
                      <div className="hidden lg:block w-px h-8 bg-gray-300" aria-hidden="true" />
                    )}
                  </React.Fragment>
                ))}
              </TabsList>
            </Tabs>
            <div className="w-full mt-12 sm:mt-16 md:mt-20">
              <div className="flex w-full items-center justify-between flex-col sm:flex-row gap-4 sm:gap-0">
                <div className="flex items-center gap-4 sm:gap-6 text-2xl sm:text-3xl md:text-[40px] text-[#212121] font-semibold leading-[1.3] font-geist">
                  <span className="material-symbols-outlined flex items-center justify-center text-2xl sm:text-3xl md:text-[40px] flex-shrink-0">
                    {activeTab === TAB_IDS.PEER_COMPARISON ? 'groups' : 
                     activeTab === TAB_IDS.JURISDICTION_SNAPSHOT ? 'camera' : 'history'}
                  </span>
                  <h2 className="flex items-center">
                    {activeTab === TAB_IDS.PEER_COMPARISON ? 'Peer Comparison' : 
                     activeTab === TAB_IDS.JURISDICTION_SNAPSHOT ? `${formattedPlaceName} Snapshot` : 
                     `${formattedPlaceName} Over Time`}
                  </h2>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    className="border-2 border-[#CA0079] text-[#CA0079] hover:bg-[#CA0079] hover:text-white transition-colors flex-1 sm:flex-initial text-sm sm:text-base"
                    onClick={handleShareClick}
                  >
                    <span className="material-symbols-outlined mr-2 text-[20px] leading-[20px] flex items-center justify-center">share</span>
                    Share
                  </Button>
                  <Button 
                    className="bg-[#CA0079] text-white hover:bg-[#a0005f] transition-colors flex-1 sm:flex-initial text-sm sm:text-base"
                    onClick={onSaveClick}
                    disabled={isSaving || !selectedPlace}
                  >
                    <span className="material-symbols-outlined mr-2 text-[20px] leading-[20px] flex items-center justify-center">
                      {isSaving ? 'sync' : 'save'}
                    </span>
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
              <p className="text-[rgba(33,33,33,1)] text-lg sm:text-xl md:text-2xl lg:text-[32px] font-normal leading-[1.3] sm:leading-[42px] mt-4 sm:mt-6 font-geist">
                {activeTab === TAB_IDS.PEER_COMPARISON ? 
                  'See how your permitting process performance compares against jurisdictions similar to yours and identify opportunities for improvement.' :
                 activeTab === TAB_IDS.JURISDICTION_SNAPSHOT ?
                  `View detailed metrics and trends for ${formattedPlaceName}'s housing and permitting performance.` :
                  `Track ${formattedPlaceName}'s progress over time and identify trends in housing and permitting metrics.`
                }
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Share options modal - kept for future use */}
      <ShareOptionsPopup
        isOpen={false}
        chartName="All Charts"
        shareLink={shareLink}
        onClose={() => {}}
      />
    </>
  );
};
